import type { Event, EventHint } from '@sentry/react';

const BROWSER_EXTENSION_REJECTION_PATTERN =
    /Object Not Found Matching Id:\d+, MethodName:update/i;

export function isTransientNetworkError(event: Event): boolean {
    return (
        event.exception?.values?.some(
            (exception) =>
                exception.type === 'AxiosError' &&
                exception.value === 'Network Error'
        ) ?? false
    );
}

export function isExpectedApiRejection(event: Event, hint: EventHint): boolean {
    const isUnhandledRejection =
        event.exception?.values?.some(
            (exception) => exception.type === 'UnhandledRejection'
        ) ?? false;

    if (!isUnhandledRejection) {
        return false;
    }

    const originalException = hint.originalException;
    if (typeof originalException !== 'object' || originalException === null) {
        return false;
    }

    const code = (originalException as { code?: unknown }).code;

    if (typeof code !== 'number') {
        return false;
    }

    if (code === 401 || code === 403 || code === 409) {
        return true;
    }

    if (code === 422) {
        const message = String(
            (originalException as { message?: unknown }).message ?? ''
        );

        return /already been added/i.test(message);
    }

    return false;
}

export function isBrowserExtensionNoise(
    event: Event,
    hint: EventHint
): boolean {
    const exceptionMessage = event.exception?.values?.[0]?.value;
    if (
        typeof exceptionMessage === 'string' &&
        BROWSER_EXTENSION_REJECTION_PATTERN.test(exceptionMessage)
    ) {
        return true;
    }

    const originalException = hint.originalException;
    if (
        typeof originalException === 'string' &&
        BROWSER_EXTENSION_REJECTION_PATTERN.test(originalException)
    ) {
        return true;
    }

    return false;
}

export function shouldDropSentryEvent(
    event: Event,
    hint: EventHint
): boolean {
    return (
        isTransientNetworkError(event) ||
        isExpectedApiRejection(event, hint) ||
        isBrowserExtensionNoise(event, hint)
    );
}
